import React, { useState } from 'react';
import { TylerState, useTylerStateUpdate } from '../utils/TylerState';
import API_CONFIG from '../config/api';

interface TestResult {
  test_name: string;
  success: boolean;
  details: string;
  response_time: number;
  timestamp: string;
}

interface TestSummary {
  total_tests: number;
  passed: number;
  failed: number;
  pass_rate: string;
}

const AdminTestingPanel: React.FC = () => {
  useTylerStateUpdate();

  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [summary, setSummary] = useState<TestSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);

  // Check if user is admin (tylerhargrave@gmail.com)
  const isAdmin = TylerState.user?.email === 'tylerhargrave@gmail.com';

  if (!isAdmin) {
    return null; // Don't show panel for non-admin users
  }

  const runTests = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/testing`);

      if (!response.ok) {
        throw new Error('Testing endpoint not available (may be disabled in production)');
      }

      const data = await response.json();

      setTestResults(data.tests || []);
      setSummary(data.summary || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run tests');
      console.error('Test error:', err);
    } finally {
      setLoading(false);
    }
  };

  const cleanupTestUsers = async () => {
    if (!window.confirm('Delete all test_*@larryshelp.test users and their scenarios?')) {
      return;
    }

    setCleanupMessage('Cleaning up...');
    try {
      const url = `${API_CONFIG.BASE_URL}/testing/cleanup-test-users`;
      console.log('[CLEANUP] Sending request to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('[CLEANUP] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[CLEANUP] Response data:', data);

      if (data.status === 'success') {
        setCleanupMessage(`✅ ${data.message} (deleted: ${data.deleted_count} users, ${data.deleted_scenarios || 0} scenarios)`);
      } else {
        setCleanupMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      console.error('[CLEANUP] Error:', errorMsg);
      setCleanupMessage(`❌ Cleanup failed: ${errorMsg}. Check console for details.`);
    }
  };

  return (
    <div style={{ marginTop: '2rem', padding: '2rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
          🧪 Backend API Testing
        </h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={cleanupTestUsers}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            🗑️ Cleanup Test Users
          </button>
          <button
            onClick={runTests}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: loading ? '#9ca3af' : '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            {loading ? '🔄 Running Tests...' : '🚀 Run Backend Tests'}
          </button>
        </div>
      </div>

      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        Test all backend API endpoints in one comprehensive test suite. Tests all 5 calculators,
        authentication, mobile responsiveness, and API endpoints. Runtime: ~10 minutes.
      </p>

      {cleanupMessage && (
        <div style={{
          padding: '1rem',
          backgroundColor: cleanupMessage.startsWith('✅') ? '#d1fae5' : '#fee2e2',
          color: cleanupMessage.startsWith('✅') ? '#065f46' : '#991b1b',
          borderRadius: '6px',
          marginBottom: '1.5rem'
        }}>
          {cleanupMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            padding: '1.5rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Total Tests
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>
              {summary.total_tests}
            </div>
          </div>

          <div style={{
            padding: '1.5rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Passed
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#059669' }}>
              {summary.passed}
            </div>
          </div>

          <div style={{
            padding: '1.5rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Failed
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: summary.failed > 0 ? '#dc2626' : '#059669' }}>
              {summary.failed}
            </div>
          </div>

          <div style={{
            padding: '1.5rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Pass Rate
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: summary.pass_rate === '100.0%' ? '#059669' : '#f59e0b' }}>
              {summary.pass_rate}
            </div>
          </div>
        </div>
      )}

      {testResults.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1rem 1.5rem',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            fontWeight: '600',
            fontSize: '1.125rem'
          }}>
            Test Results
          </div>

          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {testResults.map((test, index) => (
              <div
                key={index}
                style={{
                  padding: '1rem 1.5rem',
                  borderBottom: index < testResults.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem'
                }}
              >
                <div style={{
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}>
                  {test.success ? '✅' : '❌'}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '0.25rem'
                  }}>
                    {test.test_name}
                  </div>

                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    marginBottom: '0.25rem'
                  }}>
                    {test.details}
                  </div>

                  <div style={{
                    fontSize: '0.75rem',
                    color: '#9ca3af'
                  }}>
                    Response time: {(test.response_time * 1000).toFixed(0)}ms
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && testResults.length === 0 && !error && (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          color: '#6b7280'
        }}>
          Click "Run Backend Tests" to start comprehensive testing
        </div>
      )}
    </div>
  );
};

export default AdminTestingPanel;
